import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'

import {
  AppBar,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Drawer,
  IconButton,
  List,
  Menu,
  MenuItem,
  Toolbar,
  Tooltip,
  useMediaQuery,
  useTheme
} from '@material-ui/core'
import clsx from 'clsx'

import { toast } from 'react-toastify'
import AboutModal from '../components/AboutModal'
import BackdropLoading from '../components/BackdropLoading'
import { Backendlogs } from '../components/Backendlogs'
import LanguageModal from '../components/LanguageModal'
import UserModal from '../components/UserModal'
import { AuthContext } from '../context/Auth/AuthContext'
import toastError from '../errors/toastError'
import { i18n } from '../translate/i18n'
import MainListItems from './MainListItems'

import { SocketContext } from '../context/Socket/SocketContext'

import useAuth from '../hooks/useAuth.js'

import { LastAnnoucement } from '../components/LastAnnouncement/index.js'
import ColorModeContext from '../layout/themeContext'
import api from '../services/api'

import {
  ChevronLeft,
  Clock3,
  Languages,
  Moon,
  SquareUserRound,
  Sun,
  Unplug,
  UserRound
} from 'lucide-react'

import {
  matchPath,
  useHistory,
  useParams
} from 'react-router-dom/cjs/react-router-dom.min.js'
import ChatDrawer from '../components/ChatDrawer/index.js'
import { InputSwitch } from '../components/InputSwitch/index.js'
import NotificationsCenter from '../components/NotificationsCenter/index.js'
import PendingTicketsNotifier from '../components/PendingTicketsNotifier'
import TemporaryMessagesWidget from '../components/TemporaryMessagesWidget/index.js'
import TransferNotificationListener from '../components/TransferNotificationListener'
import UserAvatar from '../components/UserAvatar/index.js'
import { TicketsContext } from '../context/Tickets/TicketsContext.js'
import { isMobile as isMobileUserAgent } from '../helpers/isMobile'
import { formatExpirationTime } from '../utils/formatExpirationTime.js'
import { useStyles } from './style'

const LoggedInLayout = ({ children, themeToggle }) => {
  const theme = useTheme()
  const history = useHistory()
  const isImpersonatedSession =
    localStorage.getItem('impersonated') === 'true'

  const { ticketId } = useParams()
  const { tickets } = useContext(TicketsContext)
  const [, setCurrentTicket] = useState(null)

  const [announcementOpen, setAnnouncementOpen] = useState(false)
  const classes = useStyles({
    hasAnnouncement: announcementOpen,
    isImpersonated: isImpersonatedSession
  })
  const [userModalOpen, setUserModalOpen] = useState(false)
  const [aboutModalOpen, setAboutModalOpen] = useState(false)
  const [anchorEl, setAnchorEl] = useState(null)
  const [menuOpen, setMenuOpen] = useState(false)
  const [languageModalOpen, setLanguageModalOpen] = useState(false)
  const { user, handleLogout, loading } = useContext(AuthContext)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [chatDrawerOpen, setChatDrawerOpen] = useState(false)
  const [drawerVariant, setDrawerVariant] = useState('permanent')

  const [userRoles, setUserRoles] = useState([])
  const canAccessBackendlogs =
    userRoles.some(roleObj => roleObj.role === 'super') ||
    isImpersonatedSession

  const { colorMode } = useContext(ColorModeContext)
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const { getCurrentUserInfo } = useAuth()
  const [, setCurrentUser] = useState({})
  const [avatarUrl, setAvatarUrl] = useState('')

  const [userFoco, setUserFoco] = useState(false)
  const socketManager = useContext(SocketContext)
  const [wsConnectionIssue, setWsConnectionIssue] = useState(false)

  const [chatNotifications, setChatNotifications] = useState([])
  const [announcementNotifications, setAnnouncementNotifications] = useState([])
  const [totalUnread, setTotalUnread] = useState(0)

  const [selectedChatData] = useState(null)
  const [selectedChatType] = useState(null)
  const chatDrawerRef = useRef(null)

  const [showClosedTickets, setShowClosedTickets] = useState(() => {
    return localStorage.getItem('showClosedTickets') === 'true'
  })
  const [companyStorageUsage, setCompanyStorageUsage] = useState({
    usedBytes: 0,
    limitBytes: null,
    usagePercent: null
  })
  const [cleanupDialogOpen, setCleanupDialogOpen] = useState(false)
  const [cleanupLoading, setCleanupLoading] = useState(false)

  const STORAGE_LIMIT_BYTES = 5 * 1024 * 1024 * 1024

  const [excludeClosedTicketsAfter, setExcludeClosedTicketsAfter] =
    useState(null)

  const userFocoIcon = '/vector/foco.svg'
  const logoIcon = '/vector/icone.svg'

  const userAvatarName = user?.name || ''

  const storageProgressValue = useMemo(
    () =>
      Math.min(Math.max(Number(companyStorageUsage.usagePercent || 0), 0), 100),
    [companyStorageUsage.usagePercent]
  )

  const lastNotifiedGB = useRef(0)

  useEffect(() => {
    const usedGB = Math.floor(
      companyStorageUsage.usedBytes / (1024 * 1024 * 1024)
    )

    // Se o uso atual em GB for maior que o último notificado e maior que 0
    if (usedGB > lastNotifiedGB.current && usedGB >= 1) {
      const message = `Aviso de Armazenamento: Você atingiu ${usedGB} GB de uso no S3 Storage.`

      // Dispara evento customizado para o NotificationsCenter
      window.dispatchEvent(
        new CustomEvent('storage-threshold-reached', {
          detail: {
            message,
            usedGB,
            timestamp: new Date().toISOString()
          }
        })
      )

      lastNotifiedGB.current = usedGB
    }

    // Se o armazenamento for limpo (abaixo do último marco), resetamos o ref
    if (usedGB < lastNotifiedGB.current) {
      lastNotifiedGB.current = usedGB
    }
  }, [companyStorageUsage.usedBytes])

  useEffect(() => {
    const handleSyncState = () => {
      const currentValue = localStorage.getItem('showClosedTickets') === 'true'
      setShowClosedTickets(currentValue)
    }

    window.addEventListener('storage', handleSyncState)
    return () => window.removeEventListener('storage', handleSyncState)
  }, [])

  const formatBytes = value => {
    const numericValue = Number(value)
    if (!Number.isFinite(numericValue) || numericValue <= 0) {
      return '0 B'
    }

    const units = ['B', 'KB', 'MB', 'GB', 'TB']
    const exponent = Math.min(
      Math.floor(Math.log(numericValue) / Math.log(1024)),
      units.length - 1
    )
    const formatted = numericValue / 1024 ** exponent

    if (exponent === 0) {
      return `${formatted.toFixed(0)} ${units[exponent]}`
    }

    if (exponent === 1) {
      return `${formatted.toFixed(formatted >= 10 ? 0 : 1)} ${units[exponent]}`
    }

    return `${formatted.toFixed(2)} ${units[exponent]}`
  }

  const handleClosedSwitchChange = useCallback(event => {
    const newValue = event.target.checked
    setShowClosedTickets(newValue)
    localStorage.setItem('showClosedTickets', String(newValue))

    // Dispara um evento customizado para que outros componentes saibam da mudança
    window.dispatchEvent(
      new CustomEvent('storage_closed_tickets', { detail: newValue })
    )
    handleCloseProfileMenu()
  }, [])

  useEffect(() => {
    const companyId = localStorage.getItem('companyId')
    const socket = socketManager.GetSocket(companyId)

    const fetchSession = async () => {
      try {
        const { data } = await api.get('/settings')
        const settings = data.find(s => s.key === 'excludeClosedTicketsAfter')
        if (settings) {
          setExcludeClosedTicketsAfter(Number(settings.value))
        }
      } catch (err) {
        toastError(err)
      }
    }

    fetchSession()

    const onSettingsUpdate = data => {
      // Validamos se a estrutura condiz com o log: args[0].setting
      if (data && data.action === 'update' && data.setting) {
        const { key, value } = data.setting

        if (key === 'excludeClosedTicketsAfter') {
          setExcludeClosedTicketsAfter(Number(value))
        }
      }
    }

    socket.on(`company-${companyId}-settings`, onSettingsUpdate)

    return () => {
      socket.disconnect()
    }
  }, [socketManager])

  useEffect(() => {
    if (ticketId && tickets.length > 0) {
      const ticket = tickets.find(t => t.id === parseInt(ticketId))
      if (ticket) setCurrentTicket(ticket)
    } else {
      setCurrentTicket(null)
    }
  }, [ticketId, tickets])

  useEffect(() => {
    let isMounted = true

    const fetchAvatar = async () => {
      if (!user?.id) {
        if (isMounted) setAvatarUrl('')
        return
      }

      try {
        const { data } = await api.get(`/users/${user.id}/avatar`)
        if (
          isMounted &&
          data &&
          typeof data === 'string' &&
          !data.includes('default-avatar.png')
        ) {
          // Adiciona o cachebuster timestamp para evitar cache antigo do navegador
          setAvatarUrl(`${data}?cb=${Date.now()}`)
        } else if (isMounted) {
          setAvatarUrl('')
        }
      } catch (err) {
        console.warn('Falha ao buscar avatar no layout')
        if (isMounted) setAvatarUrl('')
      }
    }

    fetchAvatar()

    return () => {
      isMounted = false
    }
  }, [user?.id])

  const isTicketOpenMobile = useMemo(() => {
    if (!isMobile) return false

    const match = matchPath(history.location.pathname, {
      path: '/tickets/:ticketId',
      exact: true
    })

    return !!match
  }, [history.location.pathname, isMobile])

  const handleChatOpen = (type, userData, chatData) => {
    setChatDrawerOpen(true)
    if (type === 'user' && userData) {
      if (chatDrawerRef.current) {
        chatDrawerRef.current.openSpecificChat(userData, chatData)
      }
    } else if (type === 'group' && chatData) {
      if (chatDrawerRef.current) {
        chatDrawerRef.current.openSpecificGroupChat(chatData)
      }
    }
  }

  const calculateUnreadNotifications = () => {
    const unreadChats = chatNotifications.filter(
      notification => notification.unread
    ).length
    const unreadAnnouncements = announcementNotifications.filter(
      notification => notification.unread
    ).length
    setTotalUnread(unreadChats + unreadAnnouncements)
  }

  useEffect(() => {
    if (isMobile && history.location.pathname === '/') {
      history.push('/tickets')
    }
  }, [isMobile, history])

  useEffect(() => {
    calculateUnreadNotifications()
  }, [chatNotifications, announcementNotifications])

  useEffect(() => {
    if (user?.foco !== undefined) {
      setUserFoco(user.foco)
    }
  }, [user?.foco])

  useEffect(() => {
    if (isMobile) {
      setDrawerVariant('temporary')
      setDrawerOpen(false)
    } else {
      setDrawerVariant('permanent')
    }
  }, [])

  useEffect(() => {
    getCurrentUserInfo().then(user => {
      setCurrentUser(user)
      setUserFoco(user?.foco || false)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (user?.foco !== undefined) {
      setUserFoco(user.foco)
    }
  }, [user?.foco])

  useEffect(() => {
    if (document.body.offsetWidth < 600) {
      setDrawerVariant('temporary')
    } else {
      setDrawerVariant('permanent')
    }
  }, [drawerOpen])

  useEffect(() => {
    if (!socketManager?.subscribeWsConnectionIssue) {
      return undefined
    }

    return socketManager.subscribeWsConnectionIssue(setWsConnectionIssue)
  }, [socketManager])

  useEffect(() => {
    const companyId = localStorage.getItem('companyId')
    const userId = localStorage.getItem('userId')

    const socket = socketManager.GetSocket(companyId)

    const onCompanyAuthLayout = data => {
      const impersonated = localStorage.getItem('impersonated') === 'true'
      const currentSessionIsMobile = isMobileUserAgent()
      const incomingSessionIsMobile = Boolean(data?.user?.isMobile)

      if (
        !impersonated &&
        !data?.user?.impersonated &&
        !currentSessionIsMobile &&
        !incomingSessionIsMobile &&
        data?.user?.id === +userId
      ) {
        toastError('Sua conta foi acessada em outro computador.')
        setTimeout(() => {
          localStorage.clear()
          window.location.reload()
        }, 1000)
      }
    }

    socket.on(`company-${companyId}-auth`, onCompanyAuthLayout)

    socket.emit('userStatus')
    const interval = setInterval(
      () => {
        socket.emit('userStatus')
      },
      1000 * 60 * 5
    )

    return () => {
      // socket.disconnect()
      clearInterval(interval)
    }
  }, [socketManager])

  const formattedExpiration = useMemo(() => {
    return formatExpirationTime(excludeClosedTicketsAfter)
  }, [excludeClosedTicketsAfter])

  const handleProfileMenu = useCallback(event => {
    setAnchorEl(event.currentTarget)
    setMenuOpen(true)
  }, [])

  const handleCloseProfileMenu = useCallback(() => {
    setAnchorEl(null)
    setMenuOpen(false)
  }, [])

  const handleOpenLanguageModal = () => {
    setLanguageModalOpen(true)
    handleCloseProfileMenu()
  }

  const handleCloseLanguageModal = () => {
    setLanguageModalOpen(false)
  }

  const handleOpenUserModal = () => {
    setUserModalOpen(true)
    handleCloseProfileMenu()
  }

  const handleClickLogout = () => {
    handleCloseProfileMenu()
    handleLogout()
  }

  const drawerClose = () => {
    if (document.body.offsetWidth < 600) {
      setDrawerOpen(false)
    }
  }

  const toggleColorMode = () => {
    colorMode.toggleColorMode()
  }



  const toggleFocusMode = async () => {
    try {
      const newFocusValue = !userFoco
      console.log(
        'Layout: Atualizando foco de',
        userFoco,
        'para',
        newFocusValue
      )

      const response = await api.put(`/users/${user.id}`, {
        foco: newFocusValue
      })

      if (response.status === 200) {
        setUserFoco(newFocusValue)
        console.log('Layout: Foco atualizado com sucesso na API')

        // Forçar atualização do contexto do usuário
        try {
          const updatedUser = await getCurrentUserInfo()
          console.log(
            'Layout: Usuário atualizado no contexto:',
            updatedUser?.foco
          )
        } catch (error) {
          console.error('Erro ao atualizar contexto do usuário:', error)
        }
      }
    } catch (error) {
      console.error('Erro ao atualizar modo foco:', error)
      toastError('Erro ao atualizar modo foco')
    }
  }

  const handleChooseLanguage = language => {
    localStorage.setItem('language', language)
    window.location.reload(false)
  }

  useEffect(() => {
    let isMounted = true
    const fetchUserRoles = async () => {
      if (!user?.id || user.id === 'undefined') return
      try {
        const response = await api.get(`/role/${user?.id}`)
        if (isMounted) setUserRoles(response.data)
      } catch (error) {
        console.error('Erro ao buscar roles do usuário:', error)
      }
    }
    fetchUserRoles()

    return () => {
      isMounted = false
    }
  }, [user?.id])

  useEffect(() => {
    let isMounted = true

    const loadStorageUsage = async () => {
      try {
        const { data } = await api.get('/companies/storage/usage')

        if (!isMounted) return

        const usedBytes = Number(data?.usedBytes || 0)
        setCompanyStorageUsage({
          usedBytes,
          limitBytes: STORAGE_LIMIT_BYTES,
          usagePercent: Math.min((usedBytes / STORAGE_LIMIT_BYTES) * 100, 100)
        })
      } catch (error) {
        if (!isMounted) return
        setCompanyStorageUsage({
          usedBytes: 0,
          limitBytes: STORAGE_LIMIT_BYTES,
          usagePercent: 0
        })
      }
    }

    loadStorageUsage()
    const interval = setInterval(loadStorageUsage, 1000 * 60 * 5)

    return () => {
      isMounted = false
      clearInterval(interval)
    }
  }, [])

  const storageUsageText = `${formatBytes(companyStorageUsage.usedBytes)}`

  if (loading) {
    return <BackdropLoading />
  }

  const MobileHeader = () => (
    <AppBar position="fixed" className={classes.appBar}>
      <Toolbar variant="dense" className={classes.toolbar}>
        <div className={classes.iconMobileContainer}>
          <IconButton
            edge="start"
            onClick={() => setDrawerOpen(true)}
            className={classes.iconMobile}
          >
            <img src={logoIcon} alt="logo" />
          </IconButton>
        </div>

        <div className={classes.iconButtonContainer}>
          {wsConnectionIssue && (
            <IconButton
              size="small"
              className={classes.iconMobile}
              aria-label="websocket disconnected"
            >
              <span style={{ position: 'relative', display: 'inline-flex' }}>
                <Unplug size={24} />
                <span
                  style={{
                    position: 'absolute',
                    top: -2,
                    right: -2,
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    backgroundColor: '#e53935',
                    border: '1px solid #fff'
                  }}
                />
              </span>
            </IconButton>
          )}
          <IconButton
            size="small"
            className={classes.iconMobile}
            onClick={() => history.push('/contacts')}
          >
            <SquareUserRound size={24} />
          </IconButton>
          <IconButton
            size="small"
            className={classes.iconMobile}
            aria-controls={menuOpen ? 'menu-appbar' : undefined}
            aria-haspopup="true"
            onClick={handleProfileMenu}
            aria-label="Menu de perfil do usuário"
          >
            <UserAvatar
              name={userAvatarName}
              src={avatarUrl}
              style={{ width: 28, height: 28 }}
            />
          </IconButton>
        </div>
      </Toolbar>
    </AppBar>
  )

  return (
    <div>
      <LastAnnoucement
        className={classes.lastAnnouncement}
        // offsetSetter={setTopOffset}
        offsetSetter={setAnnouncementOpen}
      />
      <div className={classes.root}>
        <Drawer
          variant={drawerVariant}
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          className={
            drawerOpen ? classes.drawerPaper : classes.drawerPaperClose
          }
          classes={{
            paper: clsx(
              classes.drawerPaper,
              !drawerOpen && classes.drawerPaperClose
            )
          }}
          //open={drawerOpen}
          onMouseEnter={() => setDrawerOpen(true)}
          onMouseLeave={() => setDrawerOpen(false)}
        >
          {/* Logo Menu */}
          <div className={classes.toolbarIcon}>
            <img
              className={drawerOpen ? classes.logo : classes.hideLogo}
              alt="logo"
            />
            <IconButton
              onClick={() => setDrawerOpen(!drawerOpen)}
              className={classes.buttonHiddenMenu}
            >
              <ChevronLeft />
            </IconButton>
          </div>

          {/* <Divider /> */}
          <List className={classes.containerWithScroll}>
            <MainListItems
              drawerClose={drawerClose}
              drawerOpen={drawerOpen}
              collapsed={!drawerOpen}
            />
          </List>
          {/* <Divider /> */}
        </Drawer>

        <UserModal
          open={userModalOpen}
          onClose={() => setUserModalOpen(false)}
          userId={user?.id}
        />
        <AboutModal
          open={aboutModalOpen}
          onClose={() => setAboutModalOpen(false)}
        />
        <LanguageModal
          open={languageModalOpen}
          onClose={handleCloseLanguageModal}
          onSelectLanguage={handleChooseLanguage}
        />

        <Dialog
          open={cleanupDialogOpen}
          onClose={() => !cleanupLoading && setCleanupDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Exclusão de Tickets Fechados</DialogTitle>
          <DialogContent>
            <DialogContentText>
              O armazenamento S3 está{' '}
              {storageProgressValue >= 90 ? 'crítico' : 'próximo do limite'} (
              {storageProgressValue.toFixed(1)}% de 5 GB utilizado).
              <br />
              <br />
              Deseja excluir permanentemente todos os tickets com status{' '}
              <strong>Fechado</strong>? Esta ação removerá os tickets e suas
              mídias do servidor e não pode ser desfeita.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setCleanupDialogOpen(false)}
              disabled={cleanupLoading}
              color="default"
            >
              Cancelar
            </Button>
            <Button
              onClick={async () => {
                setCleanupLoading(true)
                try {
                  const { data } = await api.delete(
                    '/companies/storage/closed-tickets'
                  )
                  setCleanupDialogOpen(false)
                  // Reload usage after cleanup
                  const usage = await api.get('/companies/storage/usage')
                  const usedBytes = Number(usage.data?.usedBytes || 0)
                  setCompanyStorageUsage({
                    usedBytes,
                    limitBytes: STORAGE_LIMIT_BYTES,
                    usagePercent: Math.min(
                      (usedBytes / STORAGE_LIMIT_BYTES) * 100,
                      100
                    )
                  })
                  toast.success(data.message)
                } catch (err) {
                  toastError(err)
                } finally {
                  setCleanupLoading(false)
                }
              }}
              disabled={cleanupLoading}
              color="secondary"
              variant="contained"
              startIcon={
                cleanupLoading ? (
                  <CircularProgress size={14} color="inherit" />
                ) : null
              }
            >
              {cleanupLoading ? 'Excluindo...' : 'Excluir tickets fechados'}
            </Button>
          </DialogActions>
        </Dialog>

        <Menu
          id="menu-appbar"
          anchorEl={anchorEl}
          getContentAnchorEl={null}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right'
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right'
          }}
          open={menuOpen}
          onClose={handleCloseProfileMenu}
          className={classes.menu}
          PopoverClasses={{
            paper: classes.menuPaper
          }}
        >
          <div className={classes.helloBox}>
            <span className={classes.userName}>{user?.name}</span>
            <span className={classes.userEmail}>{user?.email}</span>
            {isImpersonatedSession && (
              <span className={classes.impersonationBadge}>Sessão impersonada</span>
            )}
          </div>
          <Divider className={classes.divider} />
          {isMobile && (
            <>
              <MenuItem className={classes.menuItem}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    width: '100%'
                  }}
                >
                  <span style={{ fontSize: '14px' }}>Fechados</span>
                  <InputSwitch
                    size="small"
                    name="closed"
                    color="primary"
                    checked={showClosedTickets}
                    onChange={handleClosedSwitchChange}
                  />
                </div>
              </MenuItem>
              <Divider className={classes.divider} />
            </>
          )}
          <MenuItem onClick={handleOpenUserModal} className={classes.menuItem}>
            <UserRound size={16} />
            {i18n.t('mainDrawer.appBar.user.profile')}
          </MenuItem>
          <MenuItem
            onClick={handleOpenLanguageModal}
            className={classes.menuItem}
          >
            <Languages size={16} />
            {i18n.t('mainDrawer.appBar.user.language')}
          </MenuItem>
          <Divider className={classes.divider} />
          <MenuItem
            onClick={handleClickLogout}
            className={classes.menuItemLogout}
          >
            {isImpersonatedSession
              ? 'Voltar ao usuário principal'
              : i18n.t('mainDrawer.appBar.user.logout')}
          </MenuItem>
        </Menu>

        {isMobile ? (
          !isTicketOpenMobile && <MobileHeader />
        ) : (
          <AppBar
            position="absolute"
            className={clsx(classes.appBar, drawerOpen && classes.appBarShift)}
            color="primary"
          >
            {/* Header */}
            <Toolbar variant="dense" className={classes.toolbar}>
              {/* Icon Top Header */}
              <div className={classes.boxHeader}>
                <IconButton
                  edge="start"
                  variant="contained"
                  aria-label="open drawer"
                  onClick={() => setDrawerOpen(!drawerOpen)}
                  className={clsx(
                    classes.menuButton,
                    drawerOpen && classes.menuButtonHidden
                  )}
                >
                  <img className={classes.appLogoFavicon} alt="favicon" />
                </IconButton>

                {excludeClosedTicketsAfter > 0 && (
                  <div className={classes.boxExpirationTime}>
                    <Clock3 />
                    <span>
                      Em {formattedExpiration} suas conversas serão excluídas
                    </span>
                  </div>
                )}
              </div>

              <div className={classes.headerMenu}>
                <TemporaryMessagesWidget
                  title={storageUsageText}
                  canManage={Boolean(user?.super || user?.profile === 'admin')}
                  settings={[
                    {
                      key: 'excludeClosedTicketsAfter',
                      value: excludeClosedTicketsAfter
                    }
                  ]}
                />
                {/* Menu Language */}
                {canAccessBackendlogs && <Backendlogs />}

                {wsConnectionIssue && (
                  <Tooltip
                    title={i18n.t('mainDrawer.appBar.user.wsDisconnected') ||
                      'Conexão WebSocket perdida'}
                    arrow
                  >
                    <span>
                      <IconButton
                        aria-label="websocket disconnected"
                        className={classes.iconButton}
                      >
                        <span style={{ position: 'relative', display: 'inline-flex' }}>
                          <Unplug size={22} />
                          <span
                            style={{
                              position: 'absolute',
                              top: -2,
                              right: -2,
                              width: 8,
                              height: 8,
                              borderRadius: '50%',
                              backgroundColor: '#e53935',
                              border: '1px solid #fff'
                            }}
                          />
                        </span>
                      </IconButton>
                    </span>
                  </Tooltip>
                )}

                <Tooltip
                  title={
                    theme.mode === 'dark'
                      ? i18n.t('mainDrawer.appBar.user.lightmode')
                      : i18n.t('mainDrawer.appBar.user.darkmode')
                  }
                  arrow
                >
                  <span>
                    <IconButton
                      onClick={toggleColorMode}
                      aria-label="toggle theme"
                      className={classes.iconButton}
                    >
                      {theme.mode === 'dark' ? <Sun /> : <Moon />}
                    </IconButton>
                  </span>
                </Tooltip>

                {/* Foco temporariamente habilitado para teste */}
                <Tooltip
                  title={
                    'Com status ativo os avisos sonoros de atendimentos não visualizados será repetido a cada X minutos durante o período definido no painel.'
                  }
                  arrow
                >
                  <span>
                    <IconButton
                      onClick={toggleFocusMode}
                      aria-label="toggle focus"
                      className={classes.iconButton}
                      disabled={
                        !user.super &&
                        user.profile !== 'admin' &&
                        user.profile !== 'gestor'
                      }
                    >
                      <span
                        style={{
                          width: 24,
                          height: 24,
                          backgroundColor: userFoco
                            ? theme.palette.blue_300.main
                            : theme.palette.type === 'dark'
                              ? theme.palette.dark_50.main
                              : theme.palette.white_100.main,
                          mask: `url(${userFocoIcon}) no-repeat center / contain`,
                          WebkitMask: `url(${userFocoIcon})  no-repeat center / contain`
                        }}
                      />
                    </IconButton>
                  </span>
                </Tooltip>

                <NotificationsCenter
                  notifications={{
                    chatNotifications,
                    announcementNotifications
                  }}
                  setNotifications={{
                    setChatNotifications,
                    setAnnouncementNotifications
                  }}
                  setUnreadCount={setTotalUnread}
                  totalUnread={totalUnread}
                  handleChatOpen={handleChatOpen}
                />
                <div>
                  <IconButton
                    aria-label="Menu de perfil do usuário"
                    aria-controls={menuOpen ? 'menu-appbar' : undefined}
                    aria-haspopup="true"
                    onClick={handleProfileMenu}
                    className={classes.iconButton}
                    color="inherit"
                  >
                    <UserAvatar
                      name={userAvatarName}
                      src={avatarUrl}
                      style={{ width: 28, height: 28 }}
                    />
                  </IconButton>
                </div>
              </div>
            </Toolbar>
          </AppBar>
        )}
        <main className={classes.content}>
          <div
            className={
              isMobile ? classes.appBarSpacerMobile : classes.appBarSpacer
            }
          />
          {children ? children : null}
        </main>

        {!isMobile && (
          <div className={classes.chatDrawer}>
            <ChatDrawer
              open={chatDrawerOpen}
              setOpen={setChatDrawerOpen}
              ref={chatDrawerRef}
              initialChatType={selectedChatType}
              initialChatData={selectedChatData}
            />
          </div>
        )}

        {/* Listener para notificações de transferência */}
        <TransferNotificationListener />

        {/* Notificador de tickets pendentes */}
        <PendingTicketsNotifier />
      </div>
    </div>
  )
}

export default LoggedInLayout
